import React from "react";
import { Individual, RiskLevel } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import {
    documentTypeLabels,
    getDocumentStatusClass,
    getRiskBadgeClass,
    getVerificationStatusClass,
} from "./entityProfileConfig";

interface IndividualProfileComponentProps {
  profile: Individual;
  riskLevel?: RiskLevel;
  onEdit?: () => void;
}

export const IndividualProfileComponent: React.FC<
  IndividualProfileComponentProps
> = ({ profile, riskLevel, onEdit }) => {
  const calculateAge = (dateOfBirth?: string): string => {
    if (!dateOfBirth) return "-";
    let dob: Date;
    // Handle YYYYMMDD format (e.g. "19890910")
    if (/^\d{8}$/.test(dateOfBirth)) {
      const y = parseInt(dateOfBirth.substring(0, 4), 10);
      const m = parseInt(dateOfBirth.substring(4, 6), 10) - 1;
      const d = parseInt(dateOfBirth.substring(6, 8), 10);
      dob = new Date(y, m, d);
    } else {
      dob = new Date(dateOfBirth);
    }
    if (isNaN(dob.getTime())) return "-";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return `${age} years`;
  };

  const documents = profile.identityDocuments || [];
  const verifiedDocs = documents.filter((d) => d.status === "verified");

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Individual Profile
          </h3>
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit
            </button>
          )}
        </div>

        <div className="p-6">
          {/* Profile Header */}
          <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-semibold text-blue-600">
                {profile.firstName[0]}
                {profile.lastName[0]}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {profile.title && `${profile.title} `}
                {profile.firstName}{" "}
                {profile.middleName && `${profile.middleName} `}
                {profile.lastName}
              </h2>
              <div className="flex items-center gap-4 mt-2">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded border ${getVerificationStatusClass(profile.verificationStatus)}`}
                >
                  {profile.verificationStatus.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                {(riskLevel || profile.riskLevel) && (
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded border capitalize ${getRiskBadgeClass(riskLevel || profile.riskLevel)}`}
                  >
                    {riskLevel || profile.riskLevel} Risk
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Personal Information
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Date of Birth</dt>
                  <dd className="text-sm text-gray-900">
                    {formatDateShort(profile.dateOfBirth)}
                    {profile.dateOfBirth && (
                      <span className="text-gray-500 ml-2">
                        ({calculateAge(profile.dateOfBirth)})
                      </span>
                    )}
                  </dd>
                </div>
                {profile.placeOfBirth && (
                  <div>
                    <dt className="text-xs text-gray-500">Place of Birth</dt>
                    <dd className="text-sm text-gray-900">
                      {profile.placeOfBirth}
                    </dd>
                  </div>
                )}
                {profile.nationality && (
                  <div>
                    <dt className="text-xs text-gray-500">Nationality</dt>
                    <dd className="text-sm text-gray-900">
                      {profile.nationality}
                    </dd>
                  </div>
                )}
                {profile.occupation && (
                  <div>
                    <dt className="text-xs text-gray-500">Occupation</dt>
                    <dd className="text-sm text-gray-900">
                      {profile.occupation}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Identity Verification Status */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Identity Verification
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Verification Status</dt>
                  <dd>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded border ${getVerificationStatusClass(profile.verificationStatus)}`}
                    >
                      {profile.verificationStatus.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Documents Verified</dt>
                  <dd className="text-sm text-gray-900">
                    {profile.verificationStatus === "verified" && documents.length === 0
                      ? "Verified"
                      : `${verifiedDocs.length} of ${documents.length}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">
                    Verification Progress
                  </dt>
                  <dd className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          verifiedDocs.length >= 2 || profile.verificationStatus === "verified"
                            ? "bg-green-500"
                            : "bg-blue-500"
                        }`}
                        style={{
                          width: `${profile.verificationStatus === "verified" ? 100 : Math.min((verifiedDocs.length / 2) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {verifiedDocs.length >= 2 || profile.verificationStatus === "verified"
                        ? "Identity verified"
                        : `${2 - verifiedDocs.length} more document(s) needed`}
                    </p>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Risk Assessment */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Risk Assessment
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Risk Level</dt>
                  <dd>
                    <span
                      className={`inline-block px-3 py-1 text-sm font-medium rounded border capitalize ${getRiskBadgeClass(riskLevel || profile.riskLevel)}`}
                    >
                      {riskLevel || profile.riskLevel || "Not Assessed"}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Residential Address */}
          {profile.residentialAddress && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Residential Address
              </h4>
              <address className="text-sm text-gray-600 not-italic">
                {profile.residentialAddress.line1}
                <br />
                {profile.residentialAddress.line2 && (
                  <>
                    {profile.residentialAddress.line2}
                    <br />
                  </>
                )}
                {profile.residentialAddress.suburb},{" "}
                {profile.residentialAddress.state}{" "}
                {profile.residentialAddress.postcode}
                <br />
                {profile.residentialAddress.country}
              </address>
            </div>
          )}

          {/* Contact Details */}
          {profile.contactDetails && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Details
              </h4>
              <div className="flex flex-wrap gap-6 text-sm">
                {profile.contactDetails.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <a
                      href={`mailto:${profile.contactDetails.email}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.email}
                    </a>
                  </div>
                )}
                {profile.contactDetails.phone && (
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <a
                      href={`tel:${profile.contactDetails.phone}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.phone}
                    </a>
                  </div>
                )}
                {profile.contactDetails.mobile && (
                  <div>
                    <span className="text-gray-500">Mobile:</span>
                    <a
                      href={`tel:${profile.contactDetails.mobile}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.mobile}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Identity Documents */}
      {documents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Identity Documents
              </h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded">
                {documents.length} documents
              </span>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {documentTypeLabels[doc.type] || doc.type}
                        </div>
                        <div className="text-sm text-gray-500 font-mono">
                          {doc.documentNumber}
                        </div>
                        {doc.issuingCountry && (
                          <div className="text-xs text-gray-400 mt-1">
                            {doc.issuingCountry}
                            {doc.issuingState && ` (${doc.issuingState})`}
                          </div>
                        )}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${getDocumentStatusClass(doc.status)}`}
                    >
                      {doc.status}
                    </span>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
                    {doc.issueDate && (
                      <span>Issued: {formatDateShort(doc.issueDate)}</span>
                    )}
                    {doc.expiryDate && (
                      <span>Expires: {formatDateShort(doc.expiryDate)}</span>
                    )}
                  </div>

                  {doc.verifiedDate && (
                    <div className="mt-2 text-xs text-green-600">
                      Verified on {formatDateShort(doc.verifiedDate)}
                      {doc.verifiedBy && ` by ${doc.verifiedBy === 'SimpleKYC' || doc.verifiedBy === 'IDVerse' ? 'ARCaml Engine' : doc.verifiedBy}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualProfileComponent;
